// editorjs/tools/columns-block.js

import {v4 as uuidv4} from "uuid";
import Swal from "sweetalert2";
import {make, stopPropagationOnEditorJsControlKeys} from "@/editorjs/utils.js";

export default class Columns {

    static get enableLineBreaks() {
        return true;
    }

    constructor({data, config, api, readOnly, block}) {
        this.api = api;
        this.readOnly = readOnly;
        this.config = config || {};
        this.block = block;
        this.blockId = this.block.id;

        this.editors = {
            cols: [],
        };
        this.colWrapper = undefined;

        // 글박스(TextBox) 모드 감지 - toolbox에서 전달된 _isTextBox 플래그 확인
        this._isTextBoxMode = !!data?._isTextBox;

        // 새 블록 여부 확인: cols가 없거나, 모든 cols의 blocks가 비어있는 경우
        const hasNoContent = !data?.cols?.length ||
            data.cols.every(col => !col.blocks || col.blocks.length === 0);
        this.isNewBlock = hasNoContent;

        // 초기 설정 뷰 표시 여부: toolbox에서 방금 추가된 블록이고 textbox 모드가 아닌 경우
        // (reload 시에는 data.cols가 존재하므로 false)
        this._showInitialSetup = !data?.cols && !this._isTextBoxMode && !readOnly;

        this.keydownHandlers = new WeakMap();
        this.paragraphTransformerCleanups = new WeakMap(); // Cleanup 함수 저장용

        // data를 초기화합니다 - 깊은 복사로 독립성 보장
        this.data = this._ensureDataIndependence(data || {});

        // _isTextBox 플래그는 저장할 필요 없으므로 제거
        delete this.data._isTextBox;

        // ✨ [핵심 수정] 새로운 블록일 경우 기본값 설정
        if (this.isNewBlock) {
            if (this._isTextBoxMode) {
                // 글박스 모드: 1열, gap: 0
                this.data.cols = [{ blocks: [] }];
                this.data.style = {};
                this.data.gap = 0;
                this.data.ratios = [1];
            } else {
                // 일반 Columns 모드: 2열
                this.data.cols = [
                    { blocks: [] }, // 1열 데이터
                    { blocks: [] }  // 2열 데이터
                ];
                this.data.style = {};
                this.data.gap = this._getDefaultGap;
                this.data.ratios = this._getDefaultLayout(2); // 2열 비율 [1, 1]
            }
        } else {
            // 기존에 데이터가 있을 경우, 안전하게 기본값을 채웁니다.
            this.data.cols = this.data.cols || [];
            this.data.style = this.data.style || {};
            this.data.gap = this.data.gap ?? this._getDefaultGap;
            // ratios가 컬럼 수와 맞지 않는 경우를 대비해 재설정
            if (this.data.ratios?.length !== this.data.cols.length) {
                this.data.ratios = this._getDefaultLayout(this.data.cols.length);
            }
        }

        // 반응형 레이아웃 기본값: off
        this.data.responsive = this.data.responsive ?? false;

        // 이제 data.cols의 길이를 기준으로 numberOfColumns를 설정하면 항상 정확합니다.
        this.editors.numberOfColumns = this.data.cols.length;

        if (!this.readOnly) {
            this.onKeyUp = this.onKeyUp.bind(this);
        }

        // 글박스 모드일 때 기본 blockStyle 적용
        if (this._isTextBoxMode && this.isNewBlock) {
            this._applyTextBoxDefaultStyles();
        }
    }

    /**
     * 글박스 모드의 기본 스타일을 blockStyle tune에 적용
     */
    _applyTextBoxDefaultStyles() {
        // 글박스 기본 스타일 데이터를 block 객체에 저장
        // BlockStyleTune이 wrap() 시점에 이 데이터를 확인하여 적용
        this._textBoxDefaultStyles = {
            backgroundColor: '#f9f8f7',
            padding: { top: '8', right: '16', bottom: '8', left: '16' },
            margin: { top: '10', right: '0', bottom: '10', left: '0' },
            borderRadius: { topLeft: '3', topRight: '3', bottomRight: '3', bottomLeft: '3' }
        };
    }

    /**
     * 글박스 기본 스타일 반환 (BlockStyleTune에서 호출)
     */
    getTextBoxDefaultStyles() {
        return this._textBoxDefaultStyles || null;
    }

    destroy() {
        if (this.config.unregisterUndoManager) {
            this.config.unregisterUndoManager(this.blockId);
        }

        this._destroyAllEditors();
    }

    _destroyAllEditors() {
        this.editors.cols.forEach(editor => {
            if (editor && typeof editor.destroy === 'function') {
                const editorWrapper = editor.ui?.nodes?.wrapper;
                const keydownHandler = this.keydownHandlers.get(editor);

                if (editorWrapper && keydownHandler) {
                    editorWrapper.removeEventListener('keydown', keydownHandler);
                }

                // Paragraph Transformer cleanup 호출
                const paragraphTransformerCleanup = this.paragraphTransformerCleanups.get(editor);
                if (typeof paragraphTransformerCleanup === 'function') {
                    try {
                        paragraphTransformerCleanup();
                    } catch (error) {
                        console.error('[Columns] Paragraph Transformer cleanup 실패:', error);
                    }
                }

                // WeakMap은 editor가 GC되면 자동 정리됨 - delete 호출 불필요
                editor.destroy();
            }
        });
        this.editors.cols = [];
    }

    static get isReadOnlySupported() {
        return true;
    }

    onKeyUp(e) {
        if (e.code !== "Backspace" && e.code !== "Delete") {
            return;
        }
    }

    /**
     * 데이터 독립성을 보장하는 헬퍼 함수
     * 각 columns 인스턴스가 완전히 독립적인 데이터를 갖도록 함
     */
    _ensureDataIndependence(data) {
        // 깊은 복사를 통해 참조 공유 방지
        if (data.cols) {
            return {
                ...data,
                cols: data.cols.map(col => ({
                    blocks: col.blocks ? JSON.parse(JSON.stringify(col.blocks)) : []
                })),
                style: data.style ? {...data.style} : {},
                gap: data.gap ?? this._getDefaultGap,
                ratios: data.ratios ? [...data.ratios] : []
            };
        }
        return data;
    }

    // Helper to get default layout based on column count
    _getDefaultLayout(numCols) {
        return new Array(numCols).fill(1);
    }

    get _getDefaultGap() {
        return 10;
    }

    renderSettings() {
        // 기존 설정 아이템
        const settingsItems = this._listColumnsSettingItems();

        // Config 아이콘 (기본)
        const configIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1z" /><path d="M15 12l4 0" /><path d="M17 10l0 4" /></svg>`;

        // 최종 반환 배열
        return [
            {
                icon: configIcon,
                title: this.api.i18n.t('Config'),
                children: { items: settingsItems },
                closeOnActivate: false
            }
        ];
    }

    _listColumnsSettingItems() {
        const columnSettings = [
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3m0 1a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1z" /></svg>`,
                title: this.api.i18n.t("1 Column"),
                onActivate: () => {
                    this._updateCols(1)
                },
                closeOnActivate: true
            },
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1zm9 -1v18" /></svg>`,
                title: this.api.i18n.t("2 Columns"),
                onActivate: () => {
                    this._updateCols(2)
                },
                closeOnActivate: true
            },
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1zm6 -1v18m6 -18v18" /></svg>`,
                title: this.api.i18n.t("3 Columns"),
                onActivate: () => {
                    this._updateCols(3)
                },
                closeOnActivate: true
            },
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1z"/><path d="M12 3v18"/><path d="M13.5 18l2.5 -2.5 -2.5 -2.5"/><path d="M16 15h-6a3 3 0 1 1 0 -6h1"/></svg>`,
                title: this.api.i18n.t("Roll Columns"),
                onActivate: () => {
                    this._rollColumns()
                }
            },
            {
                type: "html",
                title: this.api.i18n.t("Responsive Layout"),
                element: this.createCheckboxInput("responsive", this.api.i18n.t("좁을 때 세로로 쌓기"))
            },
            {type: "separator"},
            {
                type: "html",
                title: this.api.i18n.t("Column Gap"),
                element: this.createNumberInput("gap", this.api.i18n.t("Column Gap"), "px", this._getDefaultGap, { min: 0, placeholder: this._getDefaultGap })
            }
        ];

        columnSettings.push({
            type: "html",
            title: this.api.i18n.t("Column Ratio"),
            element: this.createRatioInputs()
        });

        columnSettings.push({type: "separator"});
        columnSettings.push({
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>`,
            title: this.api.i18n.t("Reset"),
            hint: {title: "이 레이아웃의 스타일을 초기화합니다."},
            closeOnActivate: true,
            onActivate: () => {
                this.resetStyle();
            }
        })

        return columnSettings;
    }

    createRatioInputs() {
        const container = make('div', 'maily-editor-utils--multiple-input-container');
        const labelEl = make('label', 'maily-editor-utils--multiple-input-label', {textContent: this.api.i18n.t("Column Ratio")});

        const ratioInputs = [];

        // 해당 열 수용 아이콘 배열 가져오기
        for (let i = 0; i < this.editors.numberOfColumns; i++) {
            ratioInputs.push(this.createRatioInput(i, this.api.i18n.t(`Column Ratio ${i}`), this.editors.numberOfColumns === 1))
        }

        container.append(labelEl, ...ratioInputs);

        return container
    }

    createRatioInput(key, label, disabled = false) {
        // 인풋: 너비, 패딩, 테두리, 포커스 스타일 등 지정
        const inputEl = make('input', 'maily-editor-utils--multiple-input-default', {
            type: 'number',
            step: '1',
            value: this.data.ratios[key],
            placeholder: '1',
            min: 1,
            disabled: disabled
        });

        inputEl.addEventListener('input', () => {
            this.data.ratios[key] = inputEl.value;
            this._updateLayout();
        });

        return inputEl;
    }

    createCheckboxInput(key, label) {
        const container = make('div', 'ce-popover-item', {
            style: 'padding: 2px;'
        });
        const labelEl = make('label', '', {
            style: 'height: 34px; padding: 0 8px; display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: #374151;'
        });
        const inputEl = make('input', '', { type: 'checkbox' });
        inputEl.checked = !!this.data[key];

        inputEl.addEventListener('change', () => {
            this.data[key] = inputEl.checked;
            this._updateLayout();
        });

        labelEl.append(inputEl);
        labelEl.append(document.createTextNode(label));
        container.append(labelEl);

        return container;
    }

    createNumberInput(key, label, unit, defaultValue, attributes = {}) {
        // 컨테이너: 상대 위치 지정 및 위쪽 마진 추가
        const container = make('div', 'maily-editor-utils--input-container');
        // 라벨: 절대 위치, 배경색, 패딩, 폰트 스타일 지정
        const labelEl = make('label', 'maily-editor-utils--input-label', {textContent: `${label} (${unit})`});
        // 인풋: 너비, 패딩, 테두리, 포커스 스타일 등 지정
        const inputEl = make('input', 'maily-editor-utils--input-default', {
            type: 'number',
            step: unit === 'rem' ? '0.5' : '1',
            value: this.data[key],
            placeholder: defaultValue,
            ...attributes
        });

        inputEl.addEventListener('input', () => {
            const value = inputEl.value;
            this.data[key] = value === '' ? defaultValue : parseFloat(value);

            this._updateLayout();
        });

        container.append(labelEl, inputEl);
        return container;
    }

    _rollColumns() {
        this.data.cols.unshift(this.data.cols.pop());
        this.editors.cols.unshift(this.editors.cols.pop());
        this._rerender();
    }

    async _updateCols(num) {
        const currentCols = this.editors.numberOfColumns;

        if (num === currentCols) {
            return;
        }

        if (num < currentCols) {
            const swalTailwind = Swal.mixin({
                customClass: {
                    popup: "py-12 bg-white dark:bg-stone-800",
                    title: "text-2xl font-bold text-black dark:text-white",
                    htmlContainer: "text-base py-16",
                    icon: "border-0 mt-0",
                    confirmButton: "maily-red-button mr-3",
                    cancelButton: "maily-white-button"
                },
                buttonsStyling: false
            });

            const result = await swalTailwind.fire({
                title: this.api.i18n.t("Are you sure?"),
                text: this.api.i18n.t(`This will delete last ${currentCols - num} Column!`),
                iconHtml: "⚠️",
                showCancelButton: true,
                cancelButtonText: this.api.i18n.t("Cancel"),
                confirmButtonText: this.api.i18n.t("Yes, delete it!"),
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        // [수정] 컬럼 수를 늘릴 때, 빈 데이터 객체를 미리 추가해줍니다.
        // 기존 코드에서는 이 로직이 if (num < currentCols) 블록 안에 잘못 위치해 있었습니다.
        while (this.data.cols.length < num) {
            this.data.cols.push({ blocks: [] });
        }

        // [핵심 수정] 새로운 컬럼 수에 맞게 데이터 배열의 길이를 조정합니다.
        this.data.cols.length = num;

        // ✨ [가장 중요한 수정] 새로운 컬럼 수에 맞는 기본 비율로 ratios를 재설정합니다.
        this.data.ratios = this._getDefaultLayout(num);

        // [수정] 내부 상태(numberOfColumns)를 갱신합니다.
        this.editors.numberOfColumns = num;

        // [수정] 에디터 인스턴스 배열의 길이를 조정합니다. (rerender에서 어차피 초기화되지만 명시적으로 관리)
        this.editors.cols.length = num;

        // 변경된 사항을 화면에 다시 그립니다.
        this._rerender();
    }

    _createColumnLayout() {
        this.colWrapper.innerHTML = ''; // 기존 컬럼들을 깨끗하게 비웁니다.

        for (let index = 0; index < this.editors.numberOfColumns; index++) {
            const col = make("div", ["ce-columns__col", `ce-columns__col--${index}`], {
                id: `${this.blockId}-${index}`
            });
            this.colWrapper.appendChild(col);
        }

        this._applyLayout();
    }

    // ✨ [핵심 수정 2] 에디터 인스턴스를 초기화하는 로직을 별도 메서드로 분리합니다.
    _initializeEditors() {
        for (let index = 0; index < this.editors.numberOfColumns; index++) {
            const holderId = `${this.blockId}-${index}`;
            // 이 시점에는 holderId를 가진 요소가 DOM에 존재하므로 오류가 발생하지 않습니다.
            const columnData = this.data.cols[index] || {};

            const editorjs_instance = new this.config.EditorJsLibrary({
                defaultBlock: "paragraph",
                holder: holderId, // `col.id` 대신 생성된 ID를 사용합니다.
                tools: this.config.tools,
                tunes: this.config.tunes,
                data: {blocks: columnData.blocks || []},
                readOnly: this.readOnly,
                minHeight: 50,
                onReady: () => {
                    const editorWrapper = editorjs_instance.ui.nodes.wrapper;
                    if (!editorWrapper) {
                        console.error("Editor.js instance wrapper not found.");
                        return;
                    }
                    const keydownHandler = (event) => this.onKeyDownColumnEditor(event);
                    this.keydownHandlers.set(editorjs_instance, keydownHandler);
                    editorWrapper.addEventListener('keydown', keydownHandler);

                    if (!this.readOnly && this.isNewBlock && index === 0) {
                        editorjs_instance.caret.focus(true);
                    }
                    if (this.config.registerUndoManager) {
                        this.config.registerUndoManager(this.blockId, index, editorjs_instance);
                    }
                    // Paragraph Transformer 기능 적용 (내부 에디터)
                    if (this.config.setupParagraphTransformer) {
                        const holderElement = document.getElementById(holderId);
                        if (holderElement) {
                            const cleanup = this.config.setupParagraphTransformer(editorjs_instance, holderElement);
                            if (cleanup && typeof cleanup === 'function') {
                                this.paragraphTransformerCleanups.set(editorjs_instance, cleanup);
                            }
                        }
                    }
                },
            });

            this.editors.cols[index] = editorjs_instance;
        }
    }

    onKeyDownColumnEditor = (event) => {
        stopPropagationOnEditorJsControlKeys(event);

        // Enter, Tab, 단축키 처리
        if (event.key === "Enter" || event.key === "Tab") {
            // event.preventDefault();
            event.stopPropagation();
        }

        if (event.metaKey || event.ctrlKey) {
            const isSaveShortcut = event.key.toLowerCase() === 's';
            const isSlash = event.key.toLowerCase() === '/';
            const isUndo = event.key.toLowerCase() === 'z';
            const isRedo = (event.key.toLowerCase() === 'z' && event.shiftKey) || event.key.toLowerCase() === 'y';

            if (!isSaveShortcut && !isSlash && !isUndo && !isRedo) {
                event.stopPropagation();
            }
        }
    }

    _applyLayout() {
        // ✨ [수정] colWrapper는 이제 스타일 래퍼가 아니므로 여기에 gap을 직접 적용하지 않습니다.
        // 대신 grid-template-columns와 gap은 BlockStyleTune이 적용한 래퍼의 하위 요소인
        // ce-columns에 적용됩니다. (CSS에서 처리하는 것이 더 좋을 수 있습니다)
        // 여기서는 grid 레이아웃을 직접 제어합니다.
        this.colWrapper.style.display = 'grid';
        this.colWrapper.style.gap = this.editors.numberOfColumns > 1 ? `${this.data.gap}px` : '0px';

        const ratios = this.data.ratios.map(r => `${r}fr`).join(' ');
        this.colWrapper.style.gridTemplateColumns = ratios;

        // 반응형 레이아웃: 칼럼 폭이 (cols * 20rem)보다 작아지면 수직 스택
        // container query + 자식 grid-column: 1 / -1 로 처리 (shared/style.scss)
        if (this.data.responsive && this.editors.numberOfColumns > 1) {
            this.colWrapper.dataset.responsiveCols = this.editors.numberOfColumns;
            this.colWrapper.style.containerType = 'inline-size';
        } else {
            delete this.colWrapper.dataset.responsiveCols;
            this.colWrapper.style.containerType = '';
        }
    }

    resetStyle() {
        this.data.gap = this._getDefaultGap;
        this.data.ratios = this._getDefaultLayout(this.editors.numberOfColumns);
        this._rerender();
    }

    async _updateLayout() {
        await this.save(); // 변경된 데이터 저장 요청

        this._applyLayout();
        // collab: 설정 변경은 DOM attribute만 건드려 MutationObserver에 잡히지 않으므로
        // 블록 변경 이벤트를 명시적으로 발화해 Y.Doc 동기화를 트리거한다.
        this.block?.dispatchChange?.();
    }

    async _rerender() {
        await this.save();

        this._destroyAllEditors();

        this._createColumnLayout();

        setTimeout(() => {
            // rerender 과정에서 블록이 제거되었을 수 있으므로 안전장치를 추가합니다.
            if (this.colWrapper && this.colWrapper.isConnected) {
                this._initializeEditors();
            }
        }, 0);
        // collab: 칼럼 개수/구조 변경을 원격에 동기화
        this.block?.dispatchChange?.();
    }

    /**
     * 원격(collab) 측에서 전달된 wrapper 설정을 현재 인스턴스에 반영.
     * cols 개수 변경은 상위(_applyMapSnapshot)에서 blocks.update로 처리되므로
     * 여기서는 gap/ratios/responsive 만 다룬다. dispatchChange는 호출하지 않는다.
     * (원격 apply 경로에서 로컬 sync 루프를 방지)
     *
     * @returns {boolean} 실제로 변경이 반영됐는지
     */
    applyRemoteSettings(remoteData) {
        if (!remoteData || typeof remoteData !== 'object') return false;

        let changed = false;
        if (remoteData.gap !== undefined && this.data.gap !== remoteData.gap) {
            this.data.gap = remoteData.gap;
            changed = true;
        }
        if (Array.isArray(remoteData.ratios) &&
            JSON.stringify(this.data.ratios) !== JSON.stringify(remoteData.ratios)) {
            this.data.ratios = [...remoteData.ratios];
            changed = true;
        }
        if (remoteData.responsive !== undefined &&
            !!this.data.responsive !== !!remoteData.responsive) {
            this.data.responsive = !!remoteData.responsive;
            changed = true;
        }
        if (changed && this.colWrapper && this.colWrapper.isConnected) {
            this._applyLayout();
        }
        return changed;
    }

    render() {
        this.colWrapper = document.createElement("div");
        this.colWrapper.classList.add("ce-columns");
        // collab 원격 수신 측이 이 인스턴스의 applyRemoteSettings를 호출할 수 있도록 노출
        this.colWrapper._columnsTool = this;

        // 글박스 모드일 때 기본 스타일 데이터를 data 속성으로 전달
        // BlockStyleTune이 wrap() 시점에 이 데이터를 읽어서 적용
        if (this._textBoxDefaultStyles) {
            this.colWrapper.dataset.textboxStyles = JSON.stringify(this._textBoxDefaultStyles);
        }

        // 초기 설정 뷰: 신규 블록일 때만 설정 UI 먼저 보여주고, 확인 후 실제 칼럼 생성
        if (this._showInitialSetup) {
            this._renderSetup();
            return this.colWrapper;
        }

        this._destroyAllEditors();

        this._createColumnLayout();

        // 2. 에디터 초기화는 다음 이벤트 루프 틱으로 연기합니다.
        //    이 시점이 되면 this.colWrapper가 메인 DOM에 추가된 후입니다.
        setTimeout(() => {
            // 블록이 DOM에 여전히 연결되어 있는지 확인하는 것이 안전합니다.
            if (this.colWrapper && this.colWrapper.isConnected) {
                this._initializeEditors();
            }
        }, 0); // 0ms 지연으로도 충분합니다.

        return this.colWrapper;
    }

    /**
     * 초기 설정 뷰 — 신규 블록 생성 시 먼저 보여주는 설정 화면
     * 열 개수/간격/비율/반응형을 미리 설정하고, 미리보기 제공
     */
    /**
     * 칼럼 생성 시 BlockStyleTune 으로 전달할 스타일 키 목록
     * (수정 시 _applySetupAndRender 와 _renderSetup 양쪽에 영향)
     */
    static get SETUP_STYLE_KEYS() {
        return ['maxWidth', 'padding', 'margin', 'borderRadius', 'backgroundColor', 'textColor', 'borderWidth', 'borderColor'];
    }

    _renderSetup() {
        // setup 중에는 ce-columns 클래스를 떼서 그리드 스타일 영향을 차단
        this.colWrapper.classList.remove('ce-columns');
        this.colWrapper.innerHTML = '';

        // 임시 설정 상태 (확인 시에만 this.data / blockStyleTune 에 반영)
        const temp = {
            // 칼럼 설정
            cols: 2,
            gap: String(this._getDefaultGap),
            ratios: [1, 1],
            responsive: false,
            // 블록 스타일 (4면 속성은 단일 값으로 보관, BlockStyleTune.migrateData 가 4면 객체로 확장)
            maxWidth: '',
            padding: '',
            margin: '',
            borderRadius: '',
            backgroundColor: '',
            textColor: '',
            borderWidth: '',
            borderColor: '',
        };

        // === 컨트롤 카드 (ejs-widget--editing 의 max-width 560px 제약 내) ===
        const setup = make('div', 'ejs-widget ejs-widget--editing');
        setup.style.cssText = 'padding: 20px; margin: 16px auto 0; max-width: 560px;';
        setup.addEventListener('keydown', (e) => e.stopPropagation());
        setup.addEventListener('click', (e) => e.stopPropagation());

        // === 미리보기 영역 (setup 외부 sibling — 560px 제약을 벗어나 사용자가 입력한 max-width 를 실제로 시각화) ===
        const previewArea = make('div', 'ce-columns-setup-preview-area');
        previewArea.style.cssText = 'margin: 12px auto 16px; padding: 0 12px;';
        previewArea.addEventListener('keydown', (e) => e.stopPropagation());
        previewArea.addEventListener('click', (e) => e.stopPropagation());

        const previewHint = make('label', 'ejs-widget__label', { textContent: '미리보기' });
        previewHint.style.cssText = 'display: block; text-align: center; margin-bottom: 6px;';
        const preview = make('div', 'ce-columns-setup-preview');
        previewArea.append(previewHint, preview);

        const updatePreview = () => {
            const ratios = temp.ratios.slice(0, temp.cols).map(r => `${r}fr`).join(' ');
            const styles = [
                'display: grid',
                `grid-template-columns: ${ratios}`,
                `gap: ${temp.gap || 0}px`,
                'box-sizing: border-box',
            ];
            if (temp.maxWidth) {
                styles.push(`max-width: ${temp.maxWidth}px`);
                styles.push('margin-left: auto');
                styles.push('margin-right: auto');
            }
            if (temp.padding) styles.push(`padding: ${temp.padding}px`);
            if (temp.margin) {
                styles.push(`margin-top: ${temp.margin}px`);
                styles.push(`margin-bottom: ${temp.margin}px`);
            }
            if (temp.borderRadius) styles.push(`border-radius: ${temp.borderRadius}px`);
            if (temp.backgroundColor) styles.push(`background-color: ${temp.backgroundColor}`);
            if (temp.textColor) styles.push(`color: ${temp.textColor}`);
            if (temp.borderWidth || temp.borderColor) {
                styles.push(`border-width: ${temp.borderWidth || 1}px`);
                styles.push('border-style: solid');
                styles.push(`border-color: ${temp.borderColor || '#000000'}`);
            }
            preview.style.cssText = styles.join('; ');
            preview.innerHTML = '';
            for (let i = 0; i < temp.cols; i++) {
                const col = make('div', '', { textContent: `열 ${i + 1}` });
                col.style.cssText = 'min-height: 56px; background: rgba(241, 245, 249, 0.6); border: 1px dashed #cbd5e1; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #64748b; font-size: 12px;';
                preview.appendChild(col);
            }
        };

        // ── 공통 헬퍼 ──
        const SECTION_TITLE_CSS = 'font-size: 12px; font-weight: 600; color: #525252; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0;';
        const FIELD_LABEL_CSS = 'display: block; font-size: 11px; margin-bottom: 4px; color: #6b7280;';
        const COMPACT_INPUT_CSS = 'width: 100%; padding: 6px 8px; box-sizing: border-box;';

        const makeSectionTitle = (text) => {
            const el = make('div', '', { textContent: text });
            el.style.cssText = SECTION_TITLE_CSS;
            return el;
        };
        const makeFieldWrap = (labelText, inputEl) => {
            const wrap = make('div');
            const label = make('label', '', { textContent: labelText });
            label.style.cssText = FIELD_LABEL_CSS;
            wrap.append(label, inputEl);
            return wrap;
        };
        const makeNumericField = (labelText, key, placeholder = '') => {
            const input = make('input', 'ejs-widget__input', {
                type: 'text',
                inputmode: 'numeric',
                pattern: '[0-9]*',
                value: temp[key] || '',
                placeholder,
            });
            input.style.cssText = COMPACT_INPUT_CSS;
            input.addEventListener('input', () => {
                const cleaned = input.value.replace(/[^0-9]/g, '');
                if (cleaned !== input.value) input.value = cleaned;
                temp[key] = cleaned;
                updatePreview();
            });
            return makeFieldWrap(labelText, input);
        };
        const makeColorField = (labelText, key) => {
            const wrap = make('div');
            wrap.style.cssText = 'display: flex; gap: 4px; align-items: center;';
            const textInput = make('input', 'ejs-widget__input', {
                type: 'text',
                value: temp[key] || '',
                placeholder: '#000000',
            });
            textInput.style.cssText = 'flex: 1; min-width: 0; padding: 6px 8px; box-sizing: border-box;';
            const colorInput = make('input', '', {
                type: 'color',
                value: temp[key] || '#ffffff',
            });
            colorInput.style.cssText = 'width: 28px; height: 28px; padding: 0; border: 1px solid #d1d5db; border-radius: 4px; cursor: pointer; flex-shrink: 0; background: white;';
            textInput.addEventListener('input', () => {
                colorInput.value = textInput.value || '#ffffff';
                temp[key] = textInput.value;
                updatePreview();
            });
            colorInput.addEventListener('input', () => {
                textInput.value = colorInput.value;
                temp[key] = colorInput.value;
                updatePreview();
            });
            wrap.append(textInput, colorInput);
            return makeFieldWrap(labelText, wrap);
        };

        const rerender = () => {
            setup.innerHTML = '';

            // ===== 섹션 1: 칼럼 설정 =====
            const colSection = make('div');
            colSection.style.cssText = 'margin-bottom: 18px;';
            colSection.appendChild(makeSectionTitle('칼럼 설정'));

            // 열 개수
            const colsLabel = make('label', '', { textContent: '열 개수' });
            colsLabel.style.cssText = FIELD_LABEL_CSS;
            colSection.appendChild(colsLabel);
            const colsGroup = make('div');
            colsGroup.style.cssText = 'display: flex; gap: 4px; margin-bottom: 12px;';
            [1, 2, 3].forEach(n => {
                const btn = make('button', 'ejs-widget__btn' + (temp.cols === n ? ' ejs-widget__btn--active' : ''), {
                    type: 'button',
                    textContent: `${n}열`
                });
                btn.style.cssText = 'flex: 1;';
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    temp.cols = n;
                    while (temp.ratios.length < n) temp.ratios.push(1);
                    temp.ratios.length = n;
                    rerender();
                });
                colsGroup.appendChild(btn);
            });
            colSection.appendChild(colsGroup);

            // 2열 그리드: 열 간격 | 열 비율
            const colRow = make('div');
            colRow.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 10px 12px; margin-bottom: 10px;';
            colRow.appendChild(makeNumericField('열 간격 (px)', 'gap'));

            // 열 비율 — 1열일 때는 비활성 placeholder
            const ratioWrap = make('div');
            const ratioLabel = make('label', '', { textContent: '열 비율' });
            ratioLabel.style.cssText = FIELD_LABEL_CSS;
            ratioWrap.appendChild(ratioLabel);
            const ratioGroup = make('div');
            ratioGroup.style.cssText = 'display: flex; gap: 3px;';
            if (temp.cols > 1) {
                temp.ratios.forEach((r, i) => {
                    const input = make('input', 'ejs-widget__input', {
                        type: 'text',
                        inputmode: 'numeric',
                        pattern: '[0-9]*',
                        value: r,
                    });
                    input.style.cssText = 'flex: 1; min-width: 0; padding: 6px 4px; text-align: center; box-sizing: border-box;';
                    input.addEventListener('input', () => {
                        const cleaned = input.value.replace(/[^0-9]/g, '');
                        if (cleaned !== input.value) input.value = cleaned;
                        const v = parseInt(cleaned, 10);
                        temp.ratios[i] = isNaN(v) || v < 1 ? 1 : v;
                        updatePreview();
                    });
                    ratioGroup.appendChild(input);
                });
            } else {
                const placeholder = make('div');
                placeholder.style.cssText = 'flex: 1; padding: 6px 8px; font-size: 11px; color: #9ca3af;';
                placeholder.textContent = '2열 이상에서 설정';
                ratioGroup.appendChild(placeholder);
            }
            ratioWrap.appendChild(ratioGroup);
            colRow.appendChild(ratioWrap);

            colSection.appendChild(colRow);

            // 반응형 (cols > 1 에서만)
            if (temp.cols > 1) {
                const respLabel = make('label');
                respLabel.style.cssText = 'display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 12px; color: #374151;';
                const respInput = make('input', '', { type: 'checkbox' });
                respInput.checked = temp.responsive;
                respInput.addEventListener('change', () => { temp.responsive = respInput.checked; });
                respLabel.appendChild(respInput);
                respLabel.appendChild(document.createTextNode('좁을 때 세로로 쌓기 (반응형)'));
                colSection.appendChild(respLabel);
            }

            setup.appendChild(colSection);

            // ===== 섹션 2: 블록 스타일 =====
            const styleSection = make('div');
            styleSection.style.cssText = 'margin-bottom: 16px;';
            styleSection.appendChild(makeSectionTitle('블록 스타일'));

            const styleGrid = make('div');
            styleGrid.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 10px 12px;';
            styleGrid.appendChild(makeNumericField('최대 너비 (px)', 'maxWidth', '비움'));
            styleGrid.appendChild(makeNumericField('모서리 둥글기 (px)', 'borderRadius'));
            styleGrid.appendChild(makeNumericField('패딩 (px)', 'padding'));
            styleGrid.appendChild(makeNumericField('마진 (px)', 'margin'));
            styleGrid.appendChild(makeColorField('배경색', 'backgroundColor'));
            styleGrid.appendChild(makeColorField('글자색', 'textColor'));
            styleGrid.appendChild(makeNumericField('테두리 두께 (px)', 'borderWidth'));
            styleGrid.appendChild(makeColorField('테두리 색', 'borderColor'));

            styleSection.appendChild(styleGrid);
            setup.appendChild(styleSection);

            // 확인 버튼
            const confirmBtn = make('button', 'maily-black-button', {
                type: 'button',
                textContent: '확인'
            });
            confirmBtn.style.cssText = 'width: 100%; padding: 10px; margin-top: 4px;';
            confirmBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._applySetupAndRender(temp);
            });
            setup.appendChild(confirmBtn);

            updatePreview();
        };

        rerender();
        this.colWrapper.appendChild(setup);
        this.colWrapper.appendChild(previewArea);
    }

    /**
     * 설정 확인 후 실제 칼럼 편집기로 전환
     */
    _applySetupAndRender(temp) {
        // 임시 설정값을 this.data에 반영
        this.data.cols = Array.from({ length: temp.cols }, () => ({ blocks: [] }));
        this.data.ratios = [...temp.ratios];
        // gap 은 temp 에서 string 으로 보관 — 숫자로 정규화
        this.data.gap = parseInt(temp.gap, 10);
        if (isNaN(this.data.gap)) this.data.gap = this._getDefaultGap;
        this.data.responsive = !!temp.responsive;
        this.editors.numberOfColumns = temp.cols;

        // BlockStyleTune 에 블록 스타일 일괄 전달 (wrap 은 이미 일어났으므로 wrapper._blockStyleTune 으로 접근)
        // wrapper 는 colWrapper 의 부모 (BlockStyleTune.wrap 에서 _blockStyleTune ref 부착)
        const stylePayload = {};
        Columns.SETUP_STYLE_KEYS.forEach(key => {
            if (temp[key]) stylePayload[key] = temp[key];
        });
        if (Object.keys(stylePayload).length > 0) {
            const blockStyleWrapper = this.colWrapper.parentElement;
            const blockStyleTune = blockStyleWrapper && blockStyleWrapper._blockStyleTune;
            if (blockStyleTune && typeof blockStyleTune.applyExternalStyles === 'function') {
                blockStyleTune.applyExternalStyles(stylePayload);
            }
        }

        // 설정 완료 플래그
        this._showInitialSetup = false;

        // 설정 뷰 제거 → 실제 칼럼 레이아웃 구성
        this.colWrapper.innerHTML = '';
        this.colWrapper.classList.add('ce-columns');
        this._destroyAllEditors();
        this._createColumnLayout();

        setTimeout(() => {
            if (this.colWrapper && this.colWrapper.isConnected) {
                this._initializeEditors();
                // 첫 번째 칼럼에 포커스
                const firstEditor = this.editors.cols[0];
                if (firstEditor && firstEditor.isReady) {
                    firstEditor.isReady.then(() => {
                        try { firstEditor.caret.focus(true); } catch (e) {}
                    });
                }
            }
        }, 0);

        // collab: 초기 설정 확정 시 cols/gap/ratios/responsive + tune(blockStyle)이 동시에
        // 세팅되므로 블록 변경을 명시적으로 발화해 원격에 전파한다.
        this.block?.dispatchChange?.();
    }

    async save() {
        if (!this.readOnly) {
            for (let index = 0; index < this.editors.numberOfColumns; index++) {
                const editor = this.editors.cols[index];

                // 에디터가 유효하고 준비된 상태인지 확인
                if (editor &&
                    typeof editor.save === 'function' &&
                    editor.isReady) {
                    try {
                        // 에디터 초기화 완료 대기
                        await editor.isReady;
                        const savedBlocks = await editor.save();
                        // this.data.cols가 줄어든 경우를 대비하여 인덱스 존재 여부 확인
                        if (this.data.cols[index]) {
                            this.data.cols[index].blocks = savedBlocks.blocks;
                        }
                    } catch (e) {
                        // 에러 시 기존 데이터 유지 (조용히 실패)
                        console.warn(`[Columns] Column ${index} save skipped:`, e.message);
                    }
                } else {
                    // 에디터가 준비되지 않은 경우 기존 데이터 유지
                    if (!this.data.cols[index]) {
                        this.data.cols[index] = { blocks: [] };
                    }
                }
            }
        }

        // data.cols가 비어있지 않도록 보장
        this.data.cols = this.data.cols.slice(0, this.editors.numberOfColumns);
        return this.data;
    }

    static get toolbox() {
        return [
            {
                // 기본 Columns (2열)
                icon: `<svg width="14" height="14" viewBox="0 -1 14 14" xmlns="http://www.w3.org/2000/svg">
    <defs></defs>
    <rect x="1.194" y="-0.041" width="11.601" height="12.11" rx="1" ry="1" style="fill: rgb(255, 255, 255); stroke: rgb(0, 0, 0); stroke-width: 1.5px;"></rect>
    <path style="stroke: rgb(0, 0,  0); stroke-width:1.5px" d="M 7.032 -0.034 L 6.948 11.842"></path>
  </svg>`,
                title: "Columns"
            },
            {
                // 글박스 (1열 + 스타일 적용)
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="7" y1="8" x2="17" y2="8"></line>
                <line x1="7" y1="12" x2="17" y2="12"></line>
                <line x1="7" y1="16" x2="13" y2="16"></line>
            </svg>`,
                title: "TextBox",
                data: {
                    cols: [{ blocks: [] }],
                    gap: 0,
                    ratios: [1],
                    _isTextBox: true
                }
            }
        ];
    }
}